package com.islamicmedia.shared.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class SearchCriteria(
    val customConcerns: String = "",
    val childAgeRanges: List<String> = emptyList()
) {
    fun toPromptSection(): String {
        val concerns = listOf(
            "Islamic principles",
            "Profanity",
            "Immodest clothing",
            "Sexual content",
            "LGBTQ+ content",
            "Violence",
            "Substance abuse",
            "Gambling",
            "Magic & sorcery",
            "Disrespect to elders"
        )

        var result = concerns.joinToString("\n- ", prefix = "- ")

        val trimmedCustom = customConcerns.trim()
        if (trimmedCustom.isNotEmpty()) {
            result += "\n\nAdditionally, the user has specifically requested checking for the following:\n$trimmedCustom"
        }

        if (childAgeRanges.isNotEmpty()) {
            val ranges = childAgeRanges.joinToString(", ")
            result += "\n\nThe user's children fall in the following age ranges: $ranges. Please tailor the analysis to these specific age groups, highlighting concerns that are particularly relevant for children in each range."
        }

        return result
    }

    /**
     * Parse the customConcerns text into a list of distinct terms (max 5). Mirrors the
     * server-side parseCustomConcerns logic so client-side unit-name derivation matches.
     */
    fun parsedCustomConcerns(): List<String> =
        if (customConcerns.isBlank()) emptyList()
        else customConcerns.lines()
            .flatMap { it.split(",") }
            .map { it.trim() }
            .filter { it.isNotEmpty() }
            .distinct()
            .take(5)

    /**
     * Derive the Phase 2 unit name for a concern, used for /api/retry-concern requests.
     * Returns "concern:<category>" for standard concerns, "custom:<index>" for custom ones,
     * or null if the concern can't be matched.
     */
    fun unitNameFor(concern: Concern): String? {
        if (concern.category.startsWith("Custom: ")) {
            val customText = concern.category.removePrefix("Custom: ").trim()
            val customs = parsedCustomConcerns()
            val idx = customs.indexOfFirst { it.equals(customText, ignoreCase = true) }
            return if (idx >= 0) "custom:$idx" else null
        }
        return "concern:${concern.category}"
    }

    companion object {
        val DEFAULT = SearchCriteria()
        val AGE_RANGES = listOf("0-3", "4-6", "7-9", "10-12", "13-15", "16+")
    }
}
